import type { FC } from 'react';
import { Suspense, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { Company } from '@xing-com/crate-common-graphql-types';
import { useParams } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { BodyCopy } from '@xing-com/typography';

import {
  CompanyLoggedout,
  CompanyLoggedoutHeadline,
} from '../../components/company-loggedout';
import { PageError } from '../../components/page-error';
import { CompanyCardsSkeletonLoggedoutPage } from '../../components/skeleton';
import { FIELD_SCRAMBLING_ERROR, RESOLVE_ERROR } from '../../config/constants';
import { useCompaniesByIndustry } from '../../hooks/use-companies-by-industry';
import { useIndustryId } from '../../hooks/use-industry-id/use-industry-id';
import { usePagination } from '../../hooks/use-pagination';
import { scrollToTop } from '../../utils';
import { getStaticIndustryKey } from '../../utils/static-industry-key';
import { useNwtTrackingCompanies } from '../../utils/tracking';

const getErrorCode = (errorId: string) =>
  errorId === FIELD_SCRAMBLING_ERROR || errorId === RESOLVE_ERROR
    ? '404'
    : '503';

type IndustryParams = {
  industry: string;
  segment?: string;
};

const OldIndustryByCompaniesInner: FC = () => {
  const { industry, segment } = useParams<IndustryParams>();
  const { formatMessage } = useIntl();
  const { isLoggedIn } = useLoginState();
  const INDUSTRIES_LIMIT = 80;

  const { changePage, activePage } = usePagination();
  const trackNwtEvent = useNwtTrackingCompanies();

  const { industryId, scrambledIndustryId, scrambleSegmentId } = useIndustryId({
    industrySEOName: industry,
    segmentSEOName: segment,
  });

  const id = industry.split('-')[0];
  const scrambleId =
    scrambleSegmentId !== '-1' ? scrambleSegmentId : scrambledIndustryId;

  const { data, error } = useCompaniesByIndustry({
    id: scrambleId,
    limit: INDUSTRIES_LIMIT,
    offset: INDUSTRIES_LIMIT * (activePage - 1),
  });

  useEffect(() => {
    trackNwtEvent({
      eventSchema: 'basic',
      page: 'companies/industries/show',
      event: 'viewed_screen',
      itemUrn: `urn:x-xing:companies:industry:${id}`,
    });
    scrollToTop();
  }, []);

  if (error) {
    const errorId = error?.graphQLErrors?.[0]?.message;
    const errorCode = getErrorCode(errorId);

    return <PageError errorCode={errorCode} />;
  }

  const { collection, total } = data?.companiesByIndustry ?? {};
  const industryName = formatMessage({ id: getStaticIndustryKey(industryId) });

  return (
    <>
      <CompanyLoggedoutHeadline
        headerText={formatMessage(
          { id: 'CP_100_DIRECTORY_TITLE_INDUSTRY' },
          { industry: industryName }
        )}
        secondaryHeader={
          <BodyCopy size="medium">
            <FormattedMessage
              id="CP_100_DIRECTORY_INTRO_INDUSTRY"
              values={{ industry: industryName }}
            />
          </BodyCopy>
        }
        companyType="industry"
      />
      <CompanyLoggedout
        companies={collection?.map((item) => item?.company as Company)}
        total={total}
        authenticated={isLoggedIn}
        limit={INDUSTRIES_LIMIT}
        activePage={activePage}
        changePage={changePage}
        error={error}
      />
    </>
  );
};

export const OldIndustryByCompanies: FC = () => {
  return (
    <Suspense fallback={<CompanyCardsSkeletonLoggedoutPage />}>
      <OldIndustryByCompaniesInner />
    </Suspense>
  );
};
